import React, {useCallback, useMemo, useState} from 'react';
import Select from 'react-select';
import {
  Modal,
  PrimaryButton,
  DropdownIndicator,
  customStyles,
} from '../../../components';
import {useMutation, useQueryClient} from 'react-query';
import {createFlow} from '../../../requests/queries/flows';
import {toast} from 'react-toastify';
import {X} from 'lucide-react';

export const USE_CASES = [
  {value: 'banking', label: 'BANKING'},
  {value: 'payments', label: 'PAYMENTS'},
  {value: 'lending', label: 'LENDING'},
  {value: 'onboarding', label: 'ONBOARDING'},
];

const initialFlowData = {
  usecase: '',
  name: '',
  desc: '',
};

export default function CreateFlowModal({open, setOpen, companyId}) {
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [flowData, setFlowData] = useState({});
  const queryClient = useQueryClient();

  const {mutate, isLoading} = useMutation('create-e-flow', createFlow);

  const handleCreateFlow = useCallback(async () => {
    try {
      const data = {
        ...flowData,
        company_id: companyId,
      };
      await mutate(data, {
        onSuccess: () => {
          setSelectedUseCase(null);
          setFlowData(initialFlowData);
          queryClient?.invalidateQueries();
          setOpen(false);
          toast.success('Flow created Successfully!');
          queryClient.invalidateQueries('pr-company-details');
        },
        onError: error => {
          toast.error(error?.response?.data?.error);
        },
      });
    } catch (e) {
      console.log(e);
    }
  }, [companyId, flowData, mutate, queryClient, setOpen]);

  const isEmpty = useMemo(
    () => !flowData?.name || !flowData?.usecase || isLoading,
    [flowData?.name, flowData?.usecase, isLoading],
  );

  return (
    <Modal
      show={open}
      onClose={setOpen}
      extraModalClasses="max-w-[528px] overflow-y-auto h-fit max-h-[95%] sm:max-h-full rounded-b-none sm:rounded-b-lg absolute bottom-0 sm:relative lookup-modal"
      modalPosition="justify-center sm:justify-end"
    >
      <div>
        <header className="flex items-center bg-[#F9F9F9] justify-between py-3 px-6 rounded-t-xl border-[0.5px] border-transparent border-b-[#EFEFEF]">
          <h3 className="font-semibold text-deepBlue text-[18px]">
            Create a flow
          </h3>
          <button
            className="bg-[#EDEDED] rounded-md p-1.5"
            onClick={() => setOpen(false)}
          >
            <X size={16} color="#242424" />{' '}
          </button>
        </header>
        <div className="mt-6 text-left">
          <div className="px-6">
            <label htmlFor="flow_name" className="mt-6">
              <span className="text-sm ">Flow name</span>
              <input
                onChange={e => setFlowData({...flowData, name: e.target.value})}
                type="text"
                id="name"
                className="mt-2"
                required
                value={flowData?.name}
              />
            </label>

            <label htmlFor="flow_use_cases" className="mt-6 mb-2 relative">
              Use case
            </label>
            <Select
              defaultValue={selectedUseCase}
              onChange={data => {
                setSelectedUseCase(data);
                setFlowData({...flowData, usecase: data.value});
              }}
              options={USE_CASES}
              placeholder="Select a use case"
              className="basic-multi-select"
              classNamePrefix="react-select"
              components={{DropdownIndicator}}
              styles={customStyles}
            />

            <label htmlFor="flow_description" className="mt-6 relative">
              <p>
                Description
                {/*<span className="text-grey30">(optional)</span>*/}
              </p>
              <textarea
                id="description"
                className="mt-2 min-h-[100px] h-full"
                onChange={e => setFlowData({...flowData, desc: e.target.value})}
                value={flowData?.desc}
              />
            </label>
          </div>

          <div className="w-full mt-6 px-6 py-[14px] border-[0.5px] border-transparent border-t-[#EFEFEF]">
            <PrimaryButton
              onClick={handleCreateFlow}
              buttonText="Continue"
              className="w-full !h-10"
              loading={isLoading}
              disabled={isEmpty}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
